import { ArrowUpRight } from "lucide-react";
import { services, images } from "@/data/site";
import { Reveal } from "./motion";

export const Services = () => {
  const [featured, ...rest] = services;
  const Featured = featured.icon;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative z-10 bg-[#1C1C1C] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-3xl mb-14">
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF4500] font-bold mb-4">
            What We Do
          </p>
          <h2 className="font-display font-bold uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Right-sized moving services
          </h2>
          <p className="mt-6 text-neutral-400 text-base sm:text-lg leading-relaxed">
            Professional help for moves of all sizes — with a current specialty
            in quick, smaller, local jobs that larger national companies often
            can't prioritize.
          </p>
        </Reveal>

        <Reveal className="mb-12">
          <div
            data-testid="labor-only-notice"
            className="border-l-4 border-[#FF4500] bg-[#262626] p-6 sm:p-8"
          >
            <p className="text-neutral-200 text-base sm:text-lg leading-relaxed">
              <span className="font-display font-bold text-white">
                Duke Summers Moving provides moving labor only.
              </span>{" "}
              We do not provide or drive trucks. Customers arrange their own
              rental truck, trailer, or moving container, and we handle the
              loading, unloading, and heavy lifting. Need help choosing a truck
              size? Just ask.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-6">
          {/* Featured card */}
          <Reveal className="md:col-span-12 lg:col-span-7 lg:row-span-2">
            <div
              data-testid="service-card-featured"
              className="group relative flex flex-col lg:block h-full lg:min-h-[380px] overflow-hidden border border-[#3d3d3d] bg-[#303030]"
            >
              {/* Mobile/tablet: full-width image on top so both crew members stay fully visible */}
              <div className="lg:hidden w-full aspect-[16/9] overflow-hidden">
                <img
                  src={images.servicesSofa}
                  alt="Duke Summers Moving crew lifting a sofa into a moving truck."
                  className="h-full w-full object-cover object-center"
                />
              </div>
              {/* Desktop: full-bleed background with a subtle far-left gradient */}
              <img
                src={images.servicesSofa}
                alt="Duke Summers Moving crew lifting a sofa into a moving truck."
                className="hidden lg:block absolute inset-0 h-full w-full object-cover object-[58%_28%] group-hover:scale-105 transition-transform duration-700"
              />
              <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[#1C1C1C]/90 via-[#1C1C1C]/25 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8 sm:p-10">
                <Featured className="h-10 w-10 text-[#FF4500] mb-6 lg:[filter:drop-shadow(0_2px_6px_rgba(0,0,0,0.6))]" strokeWidth={1.5} />
                <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight lg:[text-shadow:0_2px_10px_rgba(0,0,0,0.7)]">
                  {featured.title}
                </h3>
                <p className="mt-4 text-neutral-200 max-w-md lg:[text-shadow:0_1px_8px_rgba(0,0,0,0.8)]">{featured.desc}</p>
                <a
                  href="#quote"
                  className="mt-6 inline-flex items-center gap-2 text-[#FF4500] font-semibold hover:gap-3 transition-all"
                >
                  Get a quote <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </Reveal>

          {/* Rest of cards */}
          {rest.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.title}
                delay={i * 0.05}
                className="md:col-span-6 lg:col-span-5"
              >
                <div
                  data-testid={`service-card-${i}`}
                  className="group h-full border border-[#3d3d3d] bg-[#303030] p-8 hover:-translate-y-1 hover:border-[#FF4500]/60 transition-all duration-300"
                >
                  <Icon
                    className="h-8 w-8 text-[#FF4500] mb-5 group-hover:scale-110 transition-transform"
                    strokeWidth={1.5}
                  />
                  <h3 className="font-display font-bold text-xl tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
